import type { ComponentType } from 'react';
import { useEffect } from 'react';

import { connect as formikConnect } from 'formik';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import size from 'lodash/size';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import { getCurrentPathname } from 'src/app/state/selectors';
import type { RootState } from 'src/app/state/store';

const mapStateToProps = (state: RootState) => ({
  pagepath: getCurrentPathname(state),
});

/**
 * # SegmentFormIncomplete
 *
 * This is a React component that connects to Formik context, and
 * dispatches a Segment TRACK event, ["Form Incomplete Error"], with
 * a payload that consists of fieldNames & their validation error
 * messages.
 *
 * - This happens when the form is going through Validation.
 * - @see https://jaredpalmer.com/formik/docs/guides/form-submission#validation
 *
 * Expect to use this component alongside `<GenericError />`
 *
 * @note This will NOT handle our Stripe/CreditCard forms, because we do not
 *       provide a validation schema for those.
 *
 * @renders Nothing
 *
 * @usage
 * ```ts
 * import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete'
 *
 * const MyFormRenderer = () => {
 *   return (
 *     <Form>
 *       <GenericError />
 *       <SegmentFormIncomplete />
 *
 *       <TextInput name="email" />
 *       <TextInput name="password" />
 *
 *       <Button type="submit" />
 *     </Form>
 *   );
 * }
 * ```
 */
const SegmentFormIncomplete = compose(
  connect(mapStateToProps),
  formikConnect
)(({ formik, pagepath, dispatch }: any) => {
  const { isValidating, isSubmitting, errors } = formik;
  const cleanedErrors = omit(errors, ['_generic']);

  useEffect(() => {
    const hasErrors = !isEmpty(cleanedErrors);
    if (isValidating && isSubmitting && hasErrors) {
      dispatch(
        segmentTrack(UNIFIED.formIncompleteError, {
          incomplete_fields_count: size(cleanedErrors),
          pagepath,
        })
      );
    }
  }, [isValidating, isSubmitting, cleanedErrors, pagepath, dispatch]);

  return null;
}) as ComponentType;

SegmentFormIncomplete.displayName = 'SegmentFormIncomplete';
export { SegmentFormIncomplete };
